import { HelpCircle, PlayCircle, RotateCcw, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useTour } from './TourProvider';
import type { TourId } from '../lib/tour/types';

interface TourMenuProps {
  /** Whether to show as collapsed icon only */
  collapsed?: boolean;
}

export function TourMenu({ collapsed = false }: TourMenuProps) {
  const { getAvailableTours, startTour, isTourCompleted, resetTour, resetAllTours } = useTour();

  const tours = getAvailableTours();

  const handleStartTour = (tourId: TourId) => {
    startTour(tourId);
  };

  const handleResetTour = (tourId: TourId, e: React.MouseEvent) => {
    e.stopPropagation();
    resetTour(tourId);
    // Start the tour after reset so user can see it again immediately
    startTour(tourId);
  };

  if (collapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  aria-label="사용 가이드"
                >
                  <HelpCircle className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">사용 가이드</TooltipContent>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>사용 가이드</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {tours.map((tour) => (
                <DropdownMenuItem
                  key={tour.id}
                  onClick={() => handleStartTour(tour.id)}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <PlayCircle className="w-4 h-4" />
                    <span>{tour.name}</span>
                  </div>
                  {isTourCompleted(tour.id) && (
                    <Check className="w-4 h-4 text-green-500" />
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => resetAllTours()}
                className="text-muted-foreground"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                모든 가이드 초기화
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          사용 가이드
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>사용 가이드</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tours.map((tour) => (
          <DropdownMenuItem
            key={tour.id}
            onClick={() => handleStartTour(tour.id)}
            className="flex items-center justify-between group"
          >
            <div className="flex items-center gap-2">
              <PlayCircle className="w-4 h-4" />
              <div>
                <div className="font-medium">{tour.name}</div>
                {tour.description && (
                  <div className="text-xs text-muted-foreground">{tour.description}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {isTourCompleted(tour.id) && (
                <Check className="w-4 h-4 text-green-500" />
              )}
              {isTourCompleted(tour.id) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                  onClick={(e) => handleResetTour(tour.id, e)}
                  aria-label="다시 보기"
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => resetAllTours()}
          className="text-muted-foreground"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          모든 가이드 초기화
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
